import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Pencil } from "lucide-react";
import { useStore } from "../store";
import { severityColor, statusStyle } from "../data/mockData";
import { auditorTypeStyle, eightDStatusStyle, lpaStatusStyle } from "../data/eightDData";
import { useIncidents, useEightDs, useFmeas, useLpas, useAddComment, useMarkIncidentMentionsRead, usePatchIncident } from "../api/sitroom";
import { RoleGuard, hasMinRole, isObserver } from "./RoleGuard";
import { useAuth } from "../context/AuthContext";
import { MentionPicker, detectMentionToken } from "./MentionPicker";
import { ServiceRequestSection } from "./ServiceRequestSection";
import { SortingResultsSection } from "./SortingResultsSection";
import { TmsSection } from "./TmsSection";
import { useServiceRequests } from "../api/sitroom";
import { AISuggestedNextSteps } from "./AISuggestedNextSteps";

// Iter38 — Sorting Results section wrapper. Lives outside IncidentPanel so the
// service-request fetch piggy-backs on the existing useServiceRequests query
// instead of re-fetching from this component every panel open.
const SortingResultsSectionWrapper = ({ incident }) => {
  const { data: srs = [] } = useServiceRequests(incident?.id, !!incident?.id);
  if (!incident?.id) return null;
  return <SortingResultsSection incidentId={incident.id} serviceRequests={srs} />;
};

const LinkedModules = ({ incidentId, severity }) => {
  const openEightDPanel = useStore((s) => s.openEightDPanel);
  const openLpaPanel = useStore((s) => s.openLpaPanel);
  const openFmeaPanel = useStore((s) => s.openFmeaPanel);
  const openNewEightDModal = useStore((s) => s.openNewEightDModal);
  const openNewLpaModal = useStore((s) => s.openNewLpaModal);

  const { data: eightDReports = [] } = useEightDs();
  const { data: fmeaSuggestions = [] } = useFmeas();
  const { data: lpaRequests = [] } = useLpas();

  const eight = eightDReports.find((e) => e.sourceIncident === incidentId);
  const fmea = fmeaSuggestions.find((f) => f.sourceIncident === incidentId);
  const lpa = lpaRequests.find((l) => l.sourceIncident === incidentId);

  const Card = ({ accent, label, body, actionLabel, onAction, onClick, dataTestId }) => (
    <div
      className="rounded-md p-3"
      style={{
        background: "#0d0f18",
        border: `1px solid ${onClick ? `${accent}55` : "rgba(255,255,255,0.12)"}`,
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
      data-testid={dataTestId}
    >
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.4px", textTransform: "uppercase" }}>
        {label}
      </div>
      <div className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: onClick ? accent : "#c5cad8" }}>
        {body}
      </div>
      {actionLabel && (
        <button
          onClick={(e) => { e.stopPropagation(); onAction(); }}
          className="mt-2 px-2.5 py-1 rounded"
          style={{
            background: `${accent}1c`,
            border: `1px solid ${accent}66`,
            color: accent,
            fontFamily: "'Syne', system-ui, sans-serif",
            fontWeight: 600,
            fontSize: 10,
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {eight ? (
        <Card
          accent="#5fa3e3"
          label="8D Report"
          body={`${eight.id} · ${eightDStatusStyle(eight.status).label} · ${eight.currentStep}`}
          onClick={() => openEightDPanel(eight.id)}
          dataTestId="linked-8d-card"
        />
      ) : (
        <Card
          accent="#5fa3e3"
          label="8D Report"
          body="No 8D report"
          actionLabel="Open 8D"
          onAction={() => openNewEightDModal(incidentId)}
          dataTestId="linked-8d-card"
        />
      )}
      {fmea ? (
        <Card
          accent="#c89adb"
          label="FMEA"
          body={`${fmea.id} · RPN ${fmea.severity * fmea.occurrence * fmea.detection} · ${fmea.status}`}
          onClick={() => openFmeaPanel(fmea.id)}
          dataTestId="linked-fmea-card"
        />
      ) : (
        <Card
          accent="#c89adb"
          label="FMEA"
          body="No FMEA suggestion"
          dataTestId="linked-fmea-card"
        />
      )}
      {lpa ? (
        <Card
          accent={auditorTypeStyle(lpa.auditorType).color}
          label="LPA Audit"
          body={`${lpa.id} · ${auditorTypeStyle(lpa.auditorType).label} · ${lpa.scheduledDate}`}
          onClick={() => openLpaPanel(lpa.id)}
          dataTestId="linked-lpa-card"
        />
      ) : (
        <Card
          accent="#c89adb"
          label="LPA Audit"
          body="No audit request"
          actionLabel="Request Audit"
          onAction={() => openNewLpaModal(incidentId)}
          dataTestId="linked-lpa-card"
        />
      )}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div
    className="rounded-md p-2.5"
    style={{
      background: "#0d0f18",
      border: "1px solid rgba(255,255,255,0.12)",
    }}
  >
    <div
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 9,
        letterSpacing: "1.2px",
        color: "#9198b0",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div
      className="mt-1"
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 12,
        color: "#f0f2f8",
        wordBreak: "break-word",
      }}
    >
      {value}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div
    className="mt-5 mb-2.5"
    style={{
      fontFamily: "DM Mono, monospace",
      fontSize: 10,
      letterSpacing: "1.6px",
      color: "#9198b0",
      textTransform: "uppercase",
    }}
  >
    {children}
  </div>
);

const SLABar = ({ label, status }) => {
  const colorMap = {
    met: "#27ae60",
    warning: "#f39c12",
    breach: "#E24B4A",
  };
  const safe = status || { state: "met", percent: 0, text: "—" };
  const c = colorMap[safe.state] || "#9198b0";
  return (
    <div className="flex items-center gap-3 mb-2">
      <div style={{ width: 110, fontFamily: "DM Mono, monospace", fontSize: 11, color: "#c5cad8" }}>
        {label}
      </div>
      <div className="flex-1 relative rounded-full overflow-hidden" style={{ height: 6, background: "#0d0f18" }}>
        <div
          className="absolute top-0 bottom-0 left-0 rounded-full"
          style={{ width: `${safe.percent || 0}%`, background: c, transition: "width 600ms ease" }}
        />
      </div>
      <div style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: c, minWidth: 100, textAlign: "right" }}>
        {safe.text}
      </div>
    </div>
  );
};

const Timeline = ({ events }) => {
  const dotColor = {
    red: "#E24B4A",
    blue: "#185FA5",
    amber: "#f39c12",
    green: "#27ae60",
  };
  return (
    <div className="relative pl-1">
      {events.map((e, i) => (
        <div key={i} className="relative pl-6 pb-4 last:pb-0">
          {i < events.length - 1 && (
            <span className="absolute left-[3.5px] top-3 bottom-0 w-px" style={{ background: "rgba(255,255,255,0.1)" }} />
          )}
          <span
            className="absolute left-0 top-1.5 rounded-full"
            style={{
              width: 8,
              height: 8,
              background: dotColor[e.dot],
              border: `2px solid ${dotColor[e.dot]}`,
              boxShadow: `0 0 0 3px rgba(0,0,0,0.6)`,
            }}
          />
          <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.5px" }}>
            {e.time}
          </div>
          <div style={{ fontSize: 12, color: "#cfd1d8", marginTop: 2, fontFamily: "Syne, sans-serif" }}>
            {e.text}
          </div>
        </div>
      ))}
    </div>
  );
};

const Comment = ({ c }) => (
  <div
    className="rounded-md p-3 mb-2.5"
    style={{
      background: "#0d0f18",
      border: "1px solid rgba(255,255,255,0.12)",
    }}
  >
    <div className="flex items-center gap-2.5">
      <div
        className="rounded-full flex items-center justify-center"
        style={{
          width: 20,
          height: 20,
          background: "rgba(24,95,165,0.4)",
          color: "#cfe1f6",
          fontFamily: "DM Mono, monospace",
          fontSize: 9,
        }}
      >
        {c.initials}
      </div>
      <div className="flex-1" style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#f0f2f8" }}>
        {c.author}
      </div>
      <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0" }}>{c.time}</div>
    </div>
    <div style={{ fontSize: 12, color: "#9aa1b3", marginTop: 8, lineHeight: 1.55 }}>{c.text}</div>
  </div>
);

const Badge = ({ children, bg, border, color }) => (
  <span
    style={{
      background: bg,
      border: `1px solid ${border}`,
      color,
      fontFamily: "DM Mono, monospace",
      fontSize: 9,
      padding: "2px 7px",
      borderRadius: 3,
      letterSpacing: "1px",
      textTransform: "uppercase",
      fontWeight: 500,
    }}
  >
    {children}
  </span>
);

// Edit-mode input — used in edit mode for both text and textarea fields.
const inputBaseStyle = {
  width: "100%",
  background: "#0d0f18",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 6,
  color: "#f0f2f8",
  fontFamily: "DM Mono, monospace",
  fontSize: 12,
  padding: "8px 10px",
  outline: "none",
};

const EditField = ({ label, value, onChange, placeholder, type = "text", testid }) => (
  <div>
    <div
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 9,
        letterSpacing: "1.6px",
        color: "#9198b0",
        textTransform: "uppercase",
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    {type === "textarea" ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        style={{ ...inputBaseStyle, resize: "vertical", lineHeight: 1.55 }}
        data-testid={testid}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputBaseStyle}
        data-testid={testid}
      />
    )}
  </div>
);


export const IncidentPanel = () => {
  const isPanelOpen = useStore((s) => s.isPanelOpen);
  const closePanel = useStore((s) => s.closePanel);
  const selectedIncidentId = useStore((s) => s.selectedIncidentId);
  const addToast = useStore((s) => s.addToast);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [comment, setComment] = React.useState("");
  const [commentVisibility, setCommentVisibility] = React.useState("all_parties");
  const [mentions, setMentions] = React.useState([]); // [{id, name}]
  const [mentionState, setMentionState] = React.useState(null); // {token, start, end} | null
  const textareaRef = React.useRef(null);
  const pickerRef = React.useRef(null);
  const { data: incidents = [] } = useIncidents();
  const addCommentMutation = useAddComment();
  const markReadMutation = useMarkIncidentMentionsRead();
  const patchIncidentMutation = usePatchIncident();

  const inc = incidents.find((i) => i.id === selectedIncidentId) || incidents[0];

  // ---- AI Next Steps prefill handler -----------------------------------
  // When AISuggestedNextSteps triggers a comment/contact_party action, the
  // store carries the prefill text (and optional @mention). Consume it here.
  const commentPrefill = useStore((s) => s.commentPrefill);
  const clearCommentPrefill = useStore((s) => s.clearCommentPrefill);
  React.useEffect(() => {
    if (!commentPrefill.incidentId || commentPrefill.incidentId !== inc?.id) return;
    if (!commentPrefill.text && !commentPrefill.mention) return;
    const mentionPrefix = commentPrefill.mention ? `@${commentPrefill.mention} ` : "";
    setComment(mentionPrefix + (commentPrefill.text || ""));
    if (commentPrefill.mention) {
      setMentions((prev) => {
        const exists = prev.some((m) => m.name === commentPrefill.mention);
        return exists ? prev : [...prev, { id: commentPrefill.mention, name: commentPrefill.mention }];
      });
    }
    // Focus + scroll into view on next frame so the textarea is mounted
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        const end = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(end, end);
      }
    }, 100);
    clearCommentPrefill();
  }, [commentPrefill.version, commentPrefill.incidentId, inc?.id, commentPrefill.text, commentPrefill.mention, clearCommentPrefill]);

  // ---- Edit mode --------------------------------------------------------
  const [editMode, setEditMode] = React.useState(false);
  const [editForm, setEditForm] = React.useState({});

  // Reset edit state whenever the panel is opened or the selected id changes.
  React.useEffect(() => {
    setEditMode(false);
  }, [selectedIncidentId, isPanelOpen]);

  const enterEditMode = () => {
    if (!inc) return;
    setEditForm({
      title: inc.title || "",
      description: inc.description || "",
      partNumber: inc.partNumber || "",
      line: inc.line || "",
      plant: inc.plant || "",
      impact: inc.impact || "",
      estUnits: inc.estUnits ?? "",
      estHours: inc.estHours ?? "",
      loss: inc.loss || "",
      internalRef: inc.internalRef || "",
    });
    setEditMode(true);
  };

  const updateEdit = (key, value) =>
    setEditForm((f) => ({ ...f, [key]: value }));

  const handleSaveEdit = async () => {
    if (!inc) return;
    // Only send keys that actually changed → smaller PATCH + cleaner audit row
    const patch = {};
    Object.entries(editForm).forEach(([k, v]) => {
      const original = inc[k];
      const norm = v === "" ? null : v;
      if (norm !== (original ?? null)) patch[k] = norm;
    });
    if (Object.keys(patch).length === 0) {
      setEditMode(false);
      addToast({ type: "info", title: "No changes", body: "Nothing to save." });
      return;
    }
    try {
      await patchIncidentMutation.mutateAsync({ id: inc.id, patch });
      addToast({ type: "info", title: "Incident updated", body: `${inc.id} saved` });
      setEditMode(false);
    } catch (ex) {
      const detail = ex?.response?.data?.detail || ex.message || "Save failed";
      addToast({ type: "s1", title: "Save failed", body: String(detail) });
    }
  };

  const handleCancelEdit = () => {
    setEditForm({});
    setEditMode(false);
  };

  // Mark mentions for this incident as read once it's opened
  React.useEffect(() => {
    if (isPanelOpen && inc?.id) {
      markReadMutation.mutate(inc.id);
    }
     
  }, [isPanelOpen, inc?.id]);

  if (!isPanelOpen || !inc) return null;

  const observerOnly = isObserver(user);
  const canComment = hasMinRole(user?.role, "responder");
  const visibleComments = (inc.comments || []).filter((c) =>
    observerOnly ? (c.visibility || "all_parties") === "all_parties" : true,
  );

  const sevColor = severityColor(inc.severity);
  const stStyle = statusStyle(inc.status);

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setComment(value);
    const caret = e.target.selectionStart || value.length;
    const tok = detectMentionToken(value, caret);
    setMentionState(tok);
  };

  const handleMentionSelect = (u) => {
    if (!u || !mentionState) {
      setMentionState(null);
      return;
    }
    // Replace the @<token> with @<name> + trailing space
    const { start, end } = mentionState;
    const before = comment.slice(0, start);
    const after = comment.slice(end);
    const inserted = `@${u.name} `;
    const next = before + inserted + after;
    setComment(next);
    setMentions((prev) =>
      prev.find((m) => m.id === u.id) ? prev : [...prev, { id: u.id, name: u.name }],
    );
    setMentionState(null);
    // Restore caret to end of insertion
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = before.length + inserted.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleSend = () => {
    if (!comment.trim()) return;
    const initials = (user?.name || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join("")
      .toUpperCase() || "??";
    // Keep only mentions whose name still appears in the text
    const finalMentions = mentions.filter((m) => comment.includes(`@${m.name}`));
    addCommentMutation.mutate({
      id: inc.id,
      comment: {
        initials,
        author: user?.name || "Anonymous",
        text: comment.trim(),
        visibility: commentVisibility,
        mentions: finalMentions.map((m) => m.id),
      },
    });
    const notifiedCount = finalMentions.length;
    addToast({
      type: "info",
      title: notifiedCount ? "Comment posted" : "Comment sent",
      body: notifiedCount
        ? `${notifiedCount} user${notifiedCount === 1 ? "" : "s"} notified · ${inc.id}`
        : `Posted on ${inc.id}`,
    });
    setComment("");
    setMentions([]);
    setMentionState(null);
  };

  const handleTextareaKeyDown = (e) => {
    // Let the picker consume Up/Down/Enter/Escape when open
    if (mentionState && pickerRef.current && pickerRef.current.handleKey(e)) {
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !mentionState) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleResolve = () => {
    addToast({
      type: "info",
      title: "Incident resolved",
      body: `FMEA suggestion generated for ${inc.id}`,
    });
    setTimeout(() => {
      addToast({
        type: "info",
        title: `Review FMEA suggestion for ${inc.id}`,
        body: "Tap to open the FMEA module",
        action: {
          label: "View →",
          onClick: () => {
            closePanel();
            navigate("/fmea");
          },
        },
      });
    }, 1000);
    closePanel();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[400] backdrop-enter"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
        onClick={closePanel}
        data-testid="incident-panel-backdrop"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 z-[401] panel-enter flex flex-col"
        style={{
          width: "min(540px, 100vw)",
          background: "#0d0f18",
          borderLeft: "1px solid rgba(255,255,255,0.1)",
        }}
        data-testid="incident-panel"
      >
        <header className="relative p-5 pb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <button
            onClick={closePanel}
            className="absolute right-4 top-4 flex items-center justify-center rounded-md hover:bg-white/5"
            style={{
              width: 28,
              height: 28,
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#9aa1b3",
            }}
            aria-label="Close panel"
            data-testid="close-panel-btn"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9198b0" }}>{inc.id}</span>
            <Badge
              bg={`${sevColor}22`}
              border={`${sevColor}88`}
              color={sevColor}
            >
              {inc.severity.toUpperCase()}
            </Badge>
            <Badge bg={stStyle.bg} border={stStyle.border} color={stStyle.color}>
              {stStyle.label}
            </Badge>
            {!editMode && (
              <RoleGuard minRole="responder">
                <button
                  onClick={enterEditMode}
                  className="ml-1 flex items-center justify-center rounded-md hover:bg-white/5"
                  style={{
                    width: 24,
                    height: 24,
                    border: "1px solid rgba(255,255,255,0.10)",
                    color: "#9aa1b3",
                  }}
                  aria-label="Edit incident"
                  data-testid="edit-incident-btn"
                >
                  <Pencil size={11} />
                </button>
              </RoleGuard>
            )}
          </div>
          <div
            style={{
              fontFamily: "Syne, sans-serif",
              fontWeight: 700,
              fontSize: 16,
              color: "#f0f2f8",
              paddingRight: 40,
              lineHeight: 1.35,
            }}
          >
            {inc.title}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5 pt-3">
          <AISuggestedNextSteps incident={inc} />
          {editMode ? (
            <div data-testid="incident-edit-form">
              <SectionTitle>Edit Incident</SectionTitle>
              <div className="flex flex-col gap-3">
                <EditField
                  label="Title"
                  value={editForm.title}
                  onChange={(v) => updateEdit("title", v)}
                  placeholder="Short summary…"
                  testid="edit-title"
                />
                <EditField
                  label="Description"
                  value={editForm.description}
                  onChange={(v) => updateEdit("description", v)}
                  placeholder="Detailed description of the incident…"
                  type="textarea"
                  testid="edit-description"
                />
                <div className="grid grid-cols-2 gap-3">
                  <EditField
                    label="Affected Part Numbers"
                    value={editForm.partNumber}
                    onChange={(v) => updateEdit("partNumber", v)}
                    placeholder="e.g. 11-XYZ-001"
                    testid="edit-part-number"
                  />
                  <EditField
                    label="Plant"
                    value={editForm.plant}
                    onChange={(v) => updateEdit("plant", v)}
                    placeholder="e.g. BMW Regensburg"
                    testid="edit-plant"
                  />
                  <EditField
                    label="Affected Line"
                    value={editForm.line}
                    onChange={(v) => updateEdit("line", v)}
                    placeholder="e.g. Line 7 Body Shop"
                    testid="edit-line"
                  />
                  <EditField
                    label="Production Impact"
                    value={editForm.impact}
                    onChange={(v) => updateEdit("impact", v)}
                    placeholder="e.g. 240 units/hour"
                    testid="edit-impact"
                  />
                  <EditField
                    label="Estimated Units"
                    value={editForm.estUnits}
                    onChange={(v) => updateEdit("estUnits", v)}
                    placeholder="e.g. 1200"
                    type="number"
                    testid="edit-est-units"
                  />
                  <EditField
                    label="Estimated Hours"
                    value={editForm.estHours}
                    onChange={(v) => updateEdit("estHours", v)}
                    placeholder="e.g. 4.5"
                    type="number"
                    testid="edit-est-hours"
                  />
                  <EditField
                    label="EUR Loss"
                    value={editForm.loss}
                    onChange={(v) => updateEdit("loss", v)}
                    placeholder="€"
                    testid="edit-loss"
                  />
                  <EditField
                    label="Internal Reference"
                    value={editForm.internalRef}
                    onChange={(v) => updateEdit("internalRef", v)}
                    placeholder="e.g. JIRA-1234"
                    testid="edit-internal-ref"
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <SectionTitle>Incident Details</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Part Number" value={inc.partNumber} />
                <Field label="Affected Line" value={inc.line} />
                <Field label="Production Impact" value={inc.impact} />
                <Field label="Est. EUR Loss" value={inc.loss} />
                <Field label="Template" value={inc.template} />
                <Field label="Opened By" value={inc.opener} />
              </div>

              <SectionTitle>Description</SectionTitle>
              <div
                className="rounded-md p-3"
                style={{
                  background: "#0d0f18",
                  border: "1px solid rgba(255,255,255,0.12)",
                  fontSize: 12,
                  color: "#9aa1b3",
                  lineHeight: 1.6,
                }}
              >
                {inc.description}
              </div>
            </>
          )}

          {!editMode && (
            <>
          <SectionTitle>Linked Parties</SectionTitle>
          <div
            className="rounded-md overflow-hidden"
            style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}
          >
            {(inc.linkedParties || []).map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-3 py-2.5"
                style={{ borderBottom: i < (inc.linkedParties || []).length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}
              >
                <span
                  className="inline-block rounded-full"
                  style={{ width: 7, height: 7, background: p.color }}
                />
                <span style={{ flex: 1, fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#f0f2f8" }}>
                  {p.name}
                </span>
                <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0" }}>{p.role}</span>
              </div>
            ))}
          </div>

          <SectionTitle>SLA Status — {inc.slaTier}</SectionTitle>
          <div
            className="rounded-md p-3"
            style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}
          >
            <SLABar label="Acknowledgement" status={inc.ackStatus} />
            <SLABar label="Resolution" status={inc.resStatus} />
          </div>

          <SectionTitle>Timeline</SectionTitle>
          <Timeline events={inc.timeline || []} />

          <SectionTitle>Comments</SectionTitle>
          {visibleComments.map((c, i) => (
            <Comment key={i} c={c} />
          ))}
          {visibleComments.length === 0 && (
            <div style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9198b0" }}>
              No comments yet.
            </div>
          )}

          <SectionTitle>Linked Modules</SectionTitle>
          <LinkedModules incidentId={inc.id} severity={inc.severity} />

          <ServiceRequestSection incident={inc} />

          <SortingResultsSectionWrapper incident={inc} />

          <TmsSection incident={inc} />

          {canComment ? (
            <div className="mt-3">
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={comment}
                  onChange={handleCommentChange}
                  onKeyDown={handleTextareaKeyDown}
                  onSelect={(e) => {
                    const caret = e.currentTarget.selectionStart || 0;
                    const tok = detectMentionToken(comment, caret);
                    setMentionState(tok);
                  }}
                  rows={2}
                  placeholder="Add a comment… type @ to mention someone"
                  className="w-full rounded-md px-3 py-2 resize-none"
                  style={{
                    background: "#0d0f18",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "#f0f2f8",
                    fontFamily: "DM Mono, monospace",
                    fontSize: 11,
                    lineHeight: 1.45,
                  }}
                  data-testid="comment-input"
                />
                {mentionState && (
                  <MentionPicker
                    ref={pickerRef}
                    incidentId={inc.id}
                    query={mentionState.token}
                    visibility={commentVisibility}
                    anchor={{ left: 0, bottom: 44 }}
                    onSelect={handleMentionSelect}
                  />
                )}
              </div>
              <div className="flex items-center gap-3 mt-2 flex-wrap" data-testid="comment-visibility-toggle">
                {[
                  { id: "all_parties", label: "All parties" },
                  { id: "internal", label: "Internal" },
                ].map((opt) => {
                  const active = commentVisibility === opt.id;
                  return (
                    <label
                      key={opt.id}
                      className="flex items-center gap-1.5 cursor-pointer"
                      style={{
                        fontFamily: "DM Mono, monospace",
                        fontSize: 10,
                        color: active ? "#9ec3ec" : "#9198b0",
                        letterSpacing: "0.4px",
                      }}
                    >
                      <input
                        type="radio"
                        name="comment-visibility"
                        value={opt.id}
                        checked={active}
                        onChange={() => setCommentVisibility(opt.id)}
                        data-testid={`comment-visibility-${opt.id}`}
                      />
                      {opt.label}
                    </label>
                  );
                })}
                {mentions.length > 0 && (
                  <span
                    style={{
                      fontFamily: "DM Mono, monospace",
                      fontSize: 10,
                      color: "#9ec3ec",
                      letterSpacing: "0.4px",
                    }}
                    data-testid="mention-count"
                  >
                    @{mentions.length} mention{mentions.length === 1 ? "" : "s"}
                  </span>
                )}
                <div className="flex-1" />
                <button
                  onClick={handleSend}
                  className="px-3 py-1.5 rounded-md"
                  style={{
                    background: "#185FA5",
                    color: "#fff",
                    fontFamily: "Syne, sans-serif",
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: "0.5px",
                  }}
                  data-testid="send-comment-btn"
                >
                  Send
                </button>
              </div>
            </div>
          ) : (
            <div
              className="mt-3 rounded-md px-3 py-2 flex items-center gap-2"
              style={{
                background: "rgba(243,177,74,0.08)",
                border: "1px solid rgba(243,177,74,0.30)",
                color: "#f3b14a",
                fontFamily: "DM Mono, monospace",
                fontSize: 11,
                letterSpacing: "0.5px",
              }}
              data-testid="comment-readonly-banner"
            >
              Observer — read only
            </div>
          )}
            </>
          )}
        </div>

        {editMode ? (
          <footer
            className="flex items-center gap-2 p-3"
            style={{ background: "#0d0f18", borderTop: "1px solid rgba(255,255,255,0.12)" }}
            data-testid="incident-edit-footer"
          >
            <button
              onClick={handleCancelEdit}
              className="px-3 py-2 rounded-md"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#cfd1d8",
                fontFamily: "Syne, sans-serif",
                fontWeight: 600,
                fontSize: 12,
              }}
              data-testid="edit-cancel-btn"
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              onClick={handleSaveEdit}
              disabled={patchIncidentMutation.isPending}
              className="px-3 py-2 rounded-md"
              style={{
                background: "#185FA5",
                border: "1px solid #185FA5",
                color: "#fff",
                fontFamily: "Syne, sans-serif",
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: "0.4px",
                opacity: patchIncidentMutation.isPending ? 0.6 : 1,
              }}
              data-testid="edit-save-btn"
            >
              {patchIncidentMutation.isPending ? "Saving…" : "Save Changes"}
            </button>
          </footer>
        ) : !observerOnly && (
          <footer
            className="flex items-center gap-2 p-3"
            style={{ background: "#0d0f18", borderTop: "1px solid rgba(255,255,255,0.12)" }}
            data-testid="incident-panel-footer"
          >
            <RoleGuard minRole="escalation_manager">
              <button
                className="px-3 py-2 rounded-md"
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#9aa1b3",
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 600,
                  fontSize: 11,
                }}
                data-testid="reassign-btn"
              >
                Reassign
              </button>
            </RoleGuard>
            <button
              onClick={handleResolve}
              className="px-3 py-2 rounded-md"
              style={{
                background: "rgba(39,174,96,0.12)",
                border: "1px solid rgba(39,174,96,0.4)",
                color: "#4ed084",
                fontFamily: "'Syne', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: 11,
              }}
              data-testid="resolve-btn"
            >
              ✓ Resolve
            </button>
            <div className="flex-1" />
            <RoleGuard minRole="escalation_manager">
              <button
                className="px-3 py-2 rounded-md"
                style={{
                  background: "rgba(226,75,74,0.10)",
                  border: "1px solid rgba(226,75,74,0.5)",
                  color: "#ff7e7c",
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.4px",
                }}
                data-testid="escalate-btn"
              >
                ⬆ Escalate
              </button>
            </RoleGuard>
          </footer>
        )}
      </aside>
    </>
  );
};
